import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import { Document } from '@styled-icons/heroicons-outline/Document'
import { useFlags } from 'launchdarkly-react-client-sdk'
import cloneDeep from 'lodash/cloneDeep'
import set from 'lodash/set'
import sumBy from 'lodash/sumBy'
import PropTypes from 'prop-types'
import React, { useCallback, useReducer, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import { useLazyGetInvoiceFeeListQuery } from 'api/billing/getInvoiceFeeList'
import { useUpdateInvoiceFeeMutation } from 'api/billing/updateInvoiceFee'
import BillingLockedTooltip from 'components/billing/common/lock-billing-date/BillingLockedTooltip'
import FooterButton from 'components/buttons/FooterButton'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import Loader from 'components/common/loader'
import WorkOrdersPhotos from 'components/drawer/work-orders-photos/WorkOrdersPhotos'
import { isAllowNegativeInvoiceFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { getBillingInvoiceWorkOrder } from 'middleware/actions/billing'
import { getPricingTableContent } from 'middleware/actions/pricing'
import { putIsLoading } from 'middleware/actions/response'
import { useAccountingLockValue } from 'providers/AccountingLockProvider'
import { WORK_ORDER_STATUS } from 'settings/constants/service'
import T from 'T'
import { addFormDirtyClass } from 'utils/dom'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'
import { formatCentsToDollars, formatDollarsToCents } from 'utils/price'

import FeesAdjustmentTable from './fees-adjustment/FeesAdjustmentTable'
import { generalFeeDefaultFilter } from './filter'
import WorkOrderTable from './workorder/WorkOrderTable'

import './style.scss'

const { SCHEDULED, IN_PROGRESS, SKIPPED, CANCELED, SERVICED } = WORK_ORDER_STATUS
const STATE_DELAY = 100

const InvoiceEdit = ({
  isOpen = false,
  className,
  invoiceNumber,
  accountId,
  accountName,
  invoiceDate,
  invoiceId,
  unPostedAmountCents,
  invoiceStatus,
  onInvoiceSideView,
  onClose,
  onRefreshTable,
}) => {
  const dispatch = useDispatch()
  const [getInvoiceFeeList, { isFetching: isInvoiceFeeListLoading }] = useLazyGetInvoiceFeeListQuery()
  const [updateInvoiceFee, { isLoading: isUpdateInvoiceLoading }] = useUpdateInvoiceFeeMutation()
  const { checkIsLocked } = useAccountingLockValue()
  const isLocked = checkIsLocked(invoiceDate)

  const [localState, setLocalState] = useReducer((prevState, newState) => ({ ...prevState, ...newState }), {
    isDirty: false,
    workOrderList: [],
    currentWorkOrder: {},
    showImageModel: false,
    feesList: [],
    instanceFeeList: [],
    filteredinstanceFeeList: [],
    removeFeeIds: [],
    invoiceTotal: 0,
    initialFeeTotal: 0,
  })

  const {
    isDirty,
    workOrderList,
    currentWorkOrder,
    showImageModel,
    feesList,
    instanceFeeList,
    filteredinstanceFeeList,
    removeFeeIds,
    invoiceTotal,
    initialFeeTotal,
  } = localState

  const isSubmitBtnDisabled = (() => {
    if (isDirty && removeFeeIds.length) {
      return false
    }

    if (isDirty && feesList.find(fee => fee.feeRefId)) {
      return false
    }

    return true
  })()
  const exceptions = get(currentWorkOrder, 'exceptions', [])
  const exceptionsWithPhotos = exceptions.filter(e => get(e, 'fileLocation', false))

  const handleStateChange = useCallback((key, value) => setLocalState({ [key]: value, isDirty: true }), [])

  const handleImageIconClick = useCallback((showImageModelStatus, workOrder) => {
    setLocalState({ showImageModel: showImageModelStatus, currentWorkOrder: workOrder })
  }, [])

  const adjustedTotal = (() => {
    let validFeeTotal = 0
    feesList.forEach(fee => {
      if (!Number.isNaN(parseFloat(fee.value))) {
        validFeeTotal += parseFloat(fee.value)
      }
    })
    return invoiceTotal - initialFeeTotal + validFeeTotal
  })()

  const flags = useFlags()
  const allowNegativeInvoices = isAllowNegativeInvoiceFeatureAvailable({ flags })
  const disableProceed = (!allowNegativeInvoices && adjustedTotal < 0) || isSubmitBtnDisabled

  const handleAddRemoveFee = useCallback(
    (index = '', removeId = '') => {
      if (index === '') {
        // add fee
        const feeListClone = feesList.map(fee => ({ ...fee, inlineEdit: false }))
        handleStateChange('feesList', [...feeListClone, { feeRefId: '', feeName: '', note: '', value: '', inlineEdit: true }])
        return
      }
      // remove fee

      const newState = {
        feesList: feesList.filter((fee, feeIndex) => feeIndex !== index),
        isDirty: true,
      }

      if (removeId) {
        // push deleteId
        set(newState, 'removeFeeIds', [...removeFeeIds, { feeId: removeId }])
      }

      setLocalState(newState)
    },
    [feesList, handleStateChange, removeFeeIds]
  )

  const handleAddFeeClick = useCallback(() => handleAddRemoveFee(), [handleAddRemoveFee])

  const handleRowInlineClick = useCallback(
    index =>
      handleStateChange(
        'feesList',
        feesList.map((fee, feeIndex) => (feeIndex === index ? { ...fee, inlineEdit: true } : { ...fee, inlineEdit: false }))
      ),
    [feesList, handleStateChange]
  )

  const handleRowDataChange = useCallback(
    (index, key, value, feeName, showNote, price) => {
      const feeClone = cloneDeep(feesList)
      // We can also use map to update
      set(feeClone, `[${index}].${key}`, value)

      if (showNote) {
        set(feeClone, `[${index}].showNote`, showNote)
      }

      if (feeName) {
        set(feeClone, `[${index}].feeName`, feeName)
      }

      if (price >= 0) {
        set(feeClone, `[${index}].value`, price)
      }

      handleStateChange('feesList', feeClone)
    },
    [feesList, handleStateChange]
  )

  const handleFeesResponse = useCallback(res => {
    const fees = get(res, 'fees', [])
    setLocalState({
      isDirty: false,
      removeFeeIds: [],
      feesList: fees,
      // Later manage all calculations in cents once BE updates all invoice money fields
      invoiceTotal: formatCentsToDollars(get(res, 'totalCents', 0)),
      initialFeeTotal: sumBy(fees, 'value'),
    })
  }, [])

  const handleFeeChanges = useCallback(() => {
    const payload = {
      invoiceId,
    }

    // Discard empty rows
    const validFeeRows = feesList.filter(fee => fee.feeRefId)
    if (validFeeRows.length) {
      const fees = validFeeRows.map(row => ({ ...row, valueCents: formatDollarsToCents(row.value) }))
      set(payload, 'fees', fees)
    }

    if (removeFeeIds.length) {
      set(payload, 'removeFees', removeFeeIds)
    }

    updateInvoiceFee(payload)
      .unwrap()
      .then(() => {
        toast.success(T.FEES_ADJUSTED, {
          hideProgressBar: false,
          autoClose: 4000,
          onClose: () => onRefreshTable(),
        })
        onClose(false)
      })
      .catch(handleError)
  }, [feesList, invoiceId, onClose, onRefreshTable, removeFeeIds, updateInvoiceFee])

  const handleClose = useCallback(() => onClose(false), [onClose])

  const handleBack = useCallback(() => {
    onClose(false)
    onInvoiceSideView(accountId, invoiceNumber, invoiceId, unPostedAmountCents, accountName, invoiceDate, invoiceStatus)
  }, [accountId, accountName, invoiceDate, invoiceId, invoiceNumber, invoiceStatus, onClose, onInvoiceSideView, unPostedAmountCents])

  const handlePhotosClose = useCallback(() => setLocalState({ showImageModel: false, currentWorkOrder: null }), [])

  useEffect(() => {
    if (!isOpen || !invoiceNumber || !invoiceId) {
      return
    }

    dispatch(putIsLoading(true))

    const WOPromise = new Promise(resolve => {
      const workOderBody = {
        invoiceNumber,
        requestedPage: 0,
        requestedPageSize: 100,
        statuses: [SCHEDULED, IN_PROGRESS, SKIPPED, CANCELED, SERVICED],
      }
      dispatch(
        getBillingInvoiceWorkOrder(workOderBody, workOrderResponce => {
          setLocalState({ workOrderList: workOrderResponce })
          resolve()

          setTimeout(() => {
            const woHeightElRef = document.querySelector('.invoice-edit-container.work-order-table')
            if (woHeightElRef) {
              const woHeight = document.querySelector('.invoice-edit-container.work-order-table').clientHeight
              // Subtract header, footer & work order height
              document.querySelector('.invoice-edit-container.fee-adjustment-table').style.height = `calc(100vh - 335px - ${woHeight}px)`
            }
          }, STATE_DELAY)
        })
      )
    })

    const nonSubFeePromise = new Promise(resolve => {
      dispatch(
        getPricingTableContent({ ...generalFeeDefaultFilter, invoiceId }, tableRows => {
          const rows = tableRows.map(row => ({ feeRefId: row.id, value: row.value, feeName: row.feeName, valueType: row.valueType }))
          setLocalState({ instanceFeeList: rows, filteredinstanceFeeList: rows })
          resolve()
        })
      )
    })

    const invoiceFeePromise = new Promise(resolve => {
      getInvoiceFeeList({ invoiceId })
        .unwrap()
        .then(response => handleFeesResponse(response))
        .catch(handleError)
        .finally(() => resolve())
    })

    Promise.all([WOPromise, nonSubFeePromise, invoiceFeePromise])
      .catch(handleError)
      .finally(() => dispatch(putIsLoading(false)))
  }, [dispatch, getInvoiceFeeList, handleFeesResponse, invoiceId, invoiceNumber, isOpen])

  return (
    <>
      {(isInvoiceFeeListLoading || isUpdateInvoiceLoading) && <Loader />}
      <section className={`${className} ${addFormDirtyClass(isDirty)}`}>
        <Box>
          <HHDialogTitle
            icon={<Document className="icon-w-16 cursor-pointer header-icon" />}
            title={[T.EDIT_INVOICE, accountName, invoiceNumber].join(' - ')}
            TitleTypographyProps={{
              variant: 'h6',
            }}
            onClose={handleClose}
          />
          <Divider />
        </Box>

        <div className="content common-padding-lr-3-5 transparent-scroll">
          <Box className="wo-block" mt={2}>
            <span className="heading">{T.WORK_ORDER}</span>
            <WorkOrderTable invoiceNumber={invoiceNumber} workOrderList={workOrderList} onImageIconClick={handleImageIconClick} />
          </Box>

          <Box className="fee-adjust-block" mt={4}>
            <Stack alignItems="center" flexDirection="row" justifyContent="space-between">
              <span className="heading">{T.INVOICE_FEES_ADJUSTMENTS}</span>
              <BillingLockedTooltip isLocked={isLocked}>
                <Button disabled={isLocked} size="small" sx={{ ml: 2 }} variant="outlined" onClick={handleAddFeeClick}>
                  {T.ADD}
                </Button>
              </BillingLockedTooltip>
            </Stack>

            <FeesAdjustmentTable
              adjustedTotal={adjustedTotal}
              feesList={feesList}
              filteredinstanceFeeList={filteredinstanceFeeList}
              instanceFeeList={instanceFeeList}
              onAddRemoveFee={handleAddRemoveFee}
              onRowDataChange={handleRowDataChange}
              onRowInlineClick={handleRowInlineClick}
              onStateChange={handleStateChange}
            />
          </Box>
        </div>

        <Stack
          alignItems="center"
          className="total-block common-padding-lr-3-5"
          flexDirection="row"
          justifyContent="flex-end"
          sx={{ position: 'fixed' }}
        >
          <span className="total">{T.TOTAL}</span>
          <Box className="price" sx={{ ml: 2, mr: 4 }}>
            <HHDisplayMoney formatToDollars={false} value={adjustedTotal} />
          </Box>
        </Stack>

        <Box className="footer common-padding-lr-3-5 bg-white" position="fixed">
          <Stack alignItems="center" flexDirection="row" justifyContent="space-between">
            <FooterButton
              hasBackButton
              isProceedButtonMUIType
              backButtonTitle={T.BACK}
              disabledProceed={isLocked || disableProceed}
              leftButtonTitle={T.CANCEL}
              rightButtonTitle={T.SAVE}
              tooltipTitle={isLocked ? T.BILLING_LOCKED : ''}
              onBack={handleBack}
              onClose={handleClose}
              onProceed={handleFeeChanges}
            />
          </Stack>
        </Box>

        <WorkOrdersPhotos exceptions={exceptionsWithPhotos} isOpenDrawer={showImageModel} onClose={handlePhotosClose} />
      </section>
    </>
  )
}

InvoiceEdit.propTypes = {
  isOpen: PropTypes.bool,
  className: PropTypes.string,
  invoiceNumber: PropTypes.number,
  accountId: PropTypes.string.isRequired,
  accountName: PropTypes.string,
  invoiceDate: PropTypes.string,
  invoiceId: PropTypes.string,
  unPostedAmountCents: PropTypes.number,
  invoiceStatus: PropTypes.string,
  onInvoiceSideView: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRefreshTable: PropTypes.func.isRequired,
}

export default InvoiceEdit
